import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { matchesDropdownSearch } from "../../common/dashboard/dropdownSearchMatch";
import {
  Box,
  ButtonBase,
  CircularProgress,
  ClickAwayListener,
  FormHelperText,
  MenuItem,
  Paper,
  Popper,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  invoiceDesignFontFamily,
  invoiceDesignTokens,
} from "./InvoiceDesignSystem";
import { AnchoredMenu } from "../../common/dashboard/AnchoredMenu";

export type InvoiceSearchableSelectSearchMode = "client" | "server";

export type InvoiceSearchableSelectProps<Option> = Readonly<{
  ariaLabel?: string;
  avoidTriggerOverlap?: boolean;
  disabled?: boolean;
  error?: boolean;
  footerAction?: Readonly<{ label: string; onClick: () => void }>;
  getOptionDisabled?: (option: Option) => boolean;
  getOptionKey: (option: Option) => string;
  getOptionLabel: (option: Option) => string;
  helperText?: React.ReactNode;
  id: string;
  /**
   * Design SearchableSelect `inline-search`: the closed field shows the
   * selection; when open, the field itself is the search box and the list
   * has no search row.
   */
  inlineSearch?: boolean;
  label?: React.ReactNode;
  labelledById?: string;
  loading?: boolean;
  loadingText?: string;
  /** Client search rule; the default keeps "label contains the term". */
  matchOption?: (option: Option, term: string) => boolean;
  menuSx?: SxProps<Theme>;
  noOptionsSx?: SxProps<Theme>;
  noOptionsText?: string;
  onChange: (option: Option | null) => void;
  onClear?: () => void;
  onSearchChange?: (search: string) => void;
  options: readonly Option[];
  optionRowHeight?: number;
  outlinedArrow?: boolean;
  placeholder: string;
  renderOption?: (option: Option) => React.ReactNode;
  required?: boolean;
  searchable?: boolean;
  searchMode?: InvoiceSearchableSelectSearchMode;
  searchPlaceholder: string;
  sx?: SxProps<Theme>;
  value: Option | null;
}>;

/**
 * Invoice-only single select. The closed trigger never becomes a text input.
 * Search is isolated inside the exact-width popup so the selected value stays
 * visible and stable.
 */
const InvoiceSearchableSelect = <Option,>({
  ariaLabel,
  avoidTriggerOverlap = false,
  disabled = false,
  error = false,
  footerAction,
  getOptionDisabled,
  getOptionKey,
  getOptionLabel,
  helperText,
  id,
  inlineSearch = false,
  label,
  labelledById,
  loading = false,
  loadingText = "Loading options...",
  matchOption,
  menuSx = {},
  noOptionsSx = {},
  noOptionsText = "No options found",
  onChange,
  onClear,
  onSearchChange,
  options,
  optionRowHeight = 41,
  outlinedArrow = false,
  placeholder,
  renderOption,
  required = false,
  searchable = true,
  searchMode = "client",
  searchPlaceholder,
  sx,
  value,
}: InvoiceSearchableSelectProps<Option>): React.ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [requestedActiveOptionKey, setRequestedActiveOptionKey] = useState<
    string | null
  >(null);
  const [popupWidth, setPopupWidth] = useState<number>();
  const [popupPlacement, setPopupPlacement] = useState<{
    above: boolean;
    maxHeight: number;
  } | null>(null);
  const [search, setSearch] = useState("");
  const onSearchChangeRef = useRef(onSearchChange);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const inlineInputRef = useRef<HTMLInputElement | null>(null);
  const inlineFieldRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  useEffect(() => {
    if (!disabled || (!anchorEl && !search)) {
      return;
    }

    setAnchorEl(null);
    setSearch("");
    onSearchChangeRef.current?.("");
  }, [anchorEl, disabled, search]);

  const labelId = `${id}-label`;
  const accessibleLabelId = labelledById || (label ? labelId : undefined);
  const helperTextId = `${id}-helper-text`;
  const listboxId = `${id}-listbox`;
  const selectedKey = value ? getOptionKey(value) : "";
  const visibleOptions = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();

    if (!searchable || searchMode === "server" || !term) {
      return options;
    }

    return options.filter((option) =>
      matchOption
        ? matchOption(option, term)
        : matchesDropdownSearch(getOptionLabel(option), term),
    );
  }, [getOptionLabel, matchOption, options, search, searchable, searchMode]);
  const enabledVisibleOptions = loading
    ? []
    : visibleOptions.filter((option) => !getOptionDisabled?.(option));
  const activeOptionIndex = visibleOptions.findIndex(
    (option) =>
      getOptionKey(option) === requestedActiveOptionKey &&
      !getOptionDisabled?.(option),
  );
  const activeOption =
    activeOptionIndex >= 0 ? visibleOptions[activeOptionIndex] : null;
  const activeOptionKey = activeOption ? getOptionKey(activeOption) : null;
  const activeOptionId =
    activeOptionIndex >= 0 ? `${id}-option-${activeOptionIndex}` : undefined;

  useEffect(() => {
    if (activeOptionId) {
      document
        .getElementById(activeOptionId)
        ?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeOptionId]);

  const clearSearch = () => {
    setSearch("");
    setRequestedActiveOptionKey(null);
    onSearchChange?.("");
  };

  const handleSearchInputRef = useCallback(
    (input: HTMLInputElement | null) => {
      searchInputRef.current = input;
      if (input && anchorEl) {
        input.focus({ preventScroll: true });
      }
    },
    [anchorEl],
  );

  const closeMenu = () => {
    setAnchorEl(null);
    clearSearch();
    (inlineSearch ? inlineInputRef.current : triggerRef.current)?.focus({
      preventScroll: true,
    });
  };

  const placePopup = useCallback((trigger: HTMLElement, scrollIntoRoom: boolean) => {
    const panelHeight = Math.min(355, 68 + Math.min(options.length, 7) * optionRowHeight);
    const bounds = trigger.getBoundingClientRect();
    const deficit = panelHeight - (window.innerHeight - bounds.bottom - 16);

    if (scrollIntoRoom && deficit > 0) {
      let parent = trigger.parentElement;
      while (parent && parent !== document.body) {
        if (/^(auto|scroll)$/.test(window.getComputedStyle(parent).overflowY) &&
            parent.scrollHeight > parent.clientHeight) break;
        parent = parent.parentElement;
      }
      const scroller = parent && parent !== document.body
        ? parent
        : document.scrollingElement || document.documentElement;
      scroller.scrollTop += deficit;
    }

    // Read again after scrolling. At the end of a page the remaining room may
    // require an upward popup or a shorter list, with the trigger still visible.
    const nextBounds = trigger.getBoundingClientRect();
    const below = window.innerHeight - nextBounds.bottom - 16;
    const above = nextBounds.top - 16;
    const opensAbove = below < Math.min(panelHeight, 200) && above > below;
    setPopupWidth(nextBounds.width);
    setPopupPlacement({
      above: opensAbove,
      maxHeight: Math.max(0, Math.min(panelHeight, opensAbove ? above : below)),
    });
  }, [options.length, optionRowHeight]);

  useEffect(() => {
    if (!avoidTriggerOverlap || !anchorEl) return;
    const syncPlacement = () => placePopup(anchorEl, false);
    window.addEventListener("resize", syncPlacement);
    window.addEventListener("scroll", syncPlacement, true);
    return () => {
      window.removeEventListener("resize", syncPlacement);
      window.removeEventListener("scroll", syncPlacement, true);
    };
  }, [anchorEl, avoidTriggerOverlap, placePopup]);

  const openMenu = (trigger: HTMLElement) => {
    setPopupWidth(trigger.getBoundingClientRect().width);
    if (avoidTriggerOverlap) placePopup(trigger, true);
    else setPopupPlacement(null);
    setRequestedActiveOptionKey(null);
    setAnchorEl(trigger);
  };

  const selectOption = (option: Option) => {
    if (getOptionDisabled?.(option)) {
      return;
    }

    onChange(option);
    closeMenu();
  };

  const moveActiveOption = (direction: 1 | -1) => {
    if (enabledVisibleOptions.length === 0) {
      setRequestedActiveOptionKey(null);
      return;
    }

    const currentIndex = enabledVisibleOptions.findIndex(
      (option) => getOptionKey(option) === activeOptionKey,
    );
    const nextIndex =
      currentIndex < 0
        ? direction === 1
          ? 0
          : enabledVisibleOptions.length - 1
        : (currentIndex + direction + enabledVisibleOptions.length) %
          enabledVisibleOptions.length;

    setRequestedActiveOptionKey(getOptionKey(enabledVisibleOptions[nextIndex]));
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      moveActiveOption(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (event.key === "Enter" || (!searchable && event.key === " ")) {
      event.preventDefault();
      event.stopPropagation();
      // Design inline search: Enter picks the first match when no row is
      // highlighted.
      const enterOption =
        activeOption || (inlineSearch ? enabledVisibleOptions[0] : null);
      if (enterOption) selectOption(enterOption);
    }
  };

  const canClear = Boolean(value && onClear);
  const currentOptionRowSx = {
    ...optionRowSx,
    minHeight: `${optionRowHeight}px !important`,
    lineHeight: `${Math.min(20, optionRowHeight - 20)}px`,
  };

  const displayText = value
    ? getOptionLabel(value)
    : loading
      ? loadingText
      : placeholder;

  const optionsPanel = (
    <Box
      sx={
        footerAction
          ? { flex: 1, minHeight: 0, overflowY: "auto" }
          : { display: "contents" }
      }
    >
      <Box
        aria-activedescendant={!searchable ? activeOptionId : undefined}
        aria-label={
          accessibleLabelId ? undefined : ariaLabel || placeholder
        }
        aria-labelledby={accessibleLabelId}
        id={listboxId}
        onKeyDown={!searchable ? handleOptionKeyDown : undefined}
        ref={(element: HTMLDivElement | null) => {
          listboxRef.current = element;
          if (element && anchorEl && !searchable) {
            element.focus({ preventScroll: true });
          }
        }}
        role="listbox"
        tabIndex={!searchable ? -1 : undefined}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: footerAction ? "visible" : "auto",
        }}
      >
        {loading ? (
          <MenuItem disabled role="option" sx={currentOptionRowSx}>
            {loadingText}
          </MenuItem>
        ) : visibleOptions.length === 0 ? (
          <MenuItem
            disabled
            role="option"
            sx={[
              currentOptionRowSx,
              ...(Array.isArray(noOptionsSx) ? noOptionsSx : [noOptionsSx]),
            ]}
          >
            {noOptionsText}
          </MenuItem>
        ) : (
          visibleOptions.map((option) => {
            const optionKey = getOptionKey(option);
            const optionDisabled = getOptionDisabled?.(option) || false;
            const selected = selectedKey === optionKey;
            const active = activeOptionKey === optionKey;

            return (
              <MenuItem
                aria-selected={selected}
                data-active={active ? "true" : undefined}
                disabled={optionDisabled}
                id={`${id}-option-${visibleOptions.indexOf(option)}`}
                key={optionKey}
                onClick={() => selectOption(option)}
                role="option"
                sx={[
                  currentOptionRowSx,
                  {
                    backgroundColor: active ? "#F7F7F8" : "transparent",
                    fontWeight: selected ? 600 : 400,
                  },
                ]}
              >
                {renderOption
                  ? renderOption(option)
                  : getOptionLabel(option)}
              </MenuItem>
            );
          })
        )}
      </Box>
      {footerAction ? (
        <Box
          sx={{ borderTop: "1px solid #9E9E9E", flexShrink: 0, p: "8px" }}
        >
          <ButtonBase
            onClick={() => {
              closeMenu();
              footerAction.onClick();
            }}
            sx={{
              backgroundColor: invoiceDesignTokens.gold,
              borderRadius: "8px",
              color: "#FFFFFF",
              fontFamily: invoiceDesignFontFamily,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: "18px",
              padding: "11px 16px",
              width: "100%",
              "&:hover, &.Mui-focusVisible": {
                backgroundColor: invoiceDesignTokens.goldDark,
              },
            }}
            type="button"
          >
            {footerAction.label}
          </ButtonBase>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box sx={sx}>
      {label ? (
        <Box
          component="label"
          id={labelId}
          sx={{
            color: invoiceDesignTokens.ink,
            display: "block",
            fontFamily: invoiceDesignFontFamily,
            fontSize: 16,
            fontWeight: 600,
            lineHeight: "20px",
            mb: "6px",
          }}
        >
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </Box>
      ) : null}

      <Box ref={inlineFieldRef} sx={{ position: "relative" }}>
        {inlineSearch ? (
          <>
            <Box
              aria-activedescendant={anchorEl ? activeOptionId : undefined}
              aria-autocomplete="list"
              aria-controls={anchorEl ? listboxId : undefined}
              aria-describedby={helperText ? helperTextId : undefined}
              aria-expanded={Boolean(anchorEl)}
              aria-invalid={error || undefined}
              aria-label={accessibleLabelId ? undefined : ariaLabel || placeholder}
              aria-labelledby={accessibleLabelId}
              aria-required={required || undefined}
              autoComplete="off"
              component="input"
              data-invoice-select-trigger
              disabled={disabled}
              id={id}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(event.target.value);
                setRequestedActiveOptionKey(null);
                onSearchChange?.(event.target.value);
                if (!anchorEl) openMenu(event.currentTarget);
              }}
              onClick={(event: React.MouseEvent<HTMLInputElement>) => {
                if (!anchorEl) openMenu(event.currentTarget);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (!anchorEl) {
                  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    openMenu(event.currentTarget);
                  }
                  return;
                }
                if (event.key === "Tab") {
                  setAnchorEl(null);
                  clearSearch();
                  return;
                }
                handleOptionKeyDown(event);
              }}
              placeholder={
                anchorEl && value
                  ? getOptionLabel(value)
                  : loading
                    ? loadingText
                    : placeholder
              }
              ref={inlineInputRef}
              role="combobox"
              title={value ? getOptionLabel(value) : undefined}
              type="text"
              value={anchorEl ? search : value ? getOptionLabel(value) : ""}
              sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid",
                borderColor: error ? "#E24C4C" : "#9E9E9E",
                borderRadius: "8px",
                boxSizing: "border-box",
                color: "#000000",
                cursor: disabled ? "not-allowed" : "text",
                display: "block",
                fontFamily: invoiceDesignFontFamily,
                fontSize: 14,
                height: 42,
                lineHeight: "20px",
                opacity: disabled ? 0.5 : 1,
                outline: "none",
                overflow: "hidden",
                padding: loading ? "10px 60px 10px 12px" : "10px 38px 10px 12px",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                "&::placeholder": { color: "#727272", opacity: 1 },
                "&:focus": { borderColor: invoiceDesignTokens.gold },
              }}
            />
            <Box
              component="span"
              data-invoice-select-indicators
              sx={{
                alignItems: "center",
                display: "inline-flex",
                gap: "6px",
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              {loading ? <CircularProgress color="inherit" size={16} /> : null}
              <svg
                aria-hidden="true"
                data-invoice-select-chevron
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                onMouseDown={(event) => {
                  event.preventDefault();
                  if (disabled) return;
                  if (anchorEl) {
                    closeMenu();
                  } else if (inlineInputRef.current) {
                    inlineInputRef.current.focus({ preventScroll: true });
                    openMenu(inlineInputRef.current);
                  }
                }}
                style={{
                  cursor: disabled ? "not-allowed" : "pointer",
                  flexShrink: 0,
                  transform: anchorEl ? "rotate(180deg)" : "none",
                  transition: "transform 120ms ease",
                }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </Box>
          </>
        ) : (
        <ButtonBase
          aria-describedby={helperText ? helperTextId : undefined}
          aria-expanded={Boolean(anchorEl)}
          aria-haspopup="listbox"
          aria-invalid={error || undefined}
          aria-label={accessibleLabelId ? undefined : ariaLabel || displayText}
          aria-labelledby={accessibleLabelId}
          aria-required={required || undefined}
          disabled={disabled}
          data-invoice-select-trigger
          id={id}
          ref={triggerRef}
          onClick={(event) => openMenu(event.currentTarget)}
          sx={{
            alignItems: "center",
            backgroundColor: "#FFFFFF",
            border: "1px solid",
            borderColor: error ? "#E24C4C" : "#9E9E9E",
            borderRadius: "8px",
            boxSizing: "border-box",
            color: value ? "#000000" : "#727272",
            display: "flex",
            fontFamily: invoiceDesignFontFamily,
            fontSize: 14,
            fontWeight: 400,
            height: 42,
            justifyContent: "space-between",
            lineHeight: "20px",
            minHeight: 42,
            minWidth: 0,
            opacity: disabled ? 0.5 : 1,
            padding: "10px 12px",
            textAlign: "left",
            width: "100%",
            "&.Mui-focusVisible": {
              borderColor: invoiceDesignTokens.gold,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayText}
          </Box>
          <Box
            component="span"
            data-invoice-select-indicators
            sx={{
              alignItems: "center",
              display: "inline-flex",
              flexShrink: 0,
              gap: "6px",
              ml: "8px",
            }}
          >
            {canClear ? <Box component="span" sx={{ width: 16 }} /> : null}
            {loading ? <CircularProgress color="inherit" size={16} /> : null}
            {outlinedArrow ? (
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  flexShrink: 0,
                  transform: anchorEl ? "rotate(180deg)" : "none",
                  transition: "transform 120ms ease",
                }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            ) : (
              <KeyboardArrowDownIcon
                aria-hidden
                sx={{
                  fontSize: 16,
                  transform: anchorEl ? "rotate(180deg)" : "none",
                  transition: "transform 120ms ease",
                }}
              />
            )}
          </Box>
        </ButtonBase>
        )}
        {canClear ? (
          <ButtonBase
            aria-label="Clear selection"
            data-invoice-select-clear
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              onClear?.();
              triggerRef.current?.focus({ preventScroll: true });
            }}
            sx={{
              backgroundColor: invoiceDesignTokens.closeRed,
              borderRadius: "50%",
              color: "#FFFFFF",
              height: 16,
              minWidth: 0,
              padding: 0,
              position: "absolute",
              right: loading ? 56 : 34,
              top: "50%",
              transform: "translateY(-50%)",
              width: 16,
              "&.Mui-focusVisible": {
                outline: "2px solid #0A0A0A",
                outlineOffset: 2,
              },
            }}
            title="Clear selection"
            type="button"
          >
            <svg
              aria-hidden="true"
              width="8"
              height="8"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.4"
              strokeLinecap="round"
            >
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </ButtonBase>
        ) : null}
      </Box>

      {helperText ? (
        <FormHelperText
          error={error}
          id={helperTextId}
          sx={{
            color: error ? "#E24C4C" : "#000000",
            fontFamily: invoiceDesignFontFamily,
            fontSize: 12,
            lineHeight: "16px",
            m: 0,
            mt: "4px",
          }}
        >
          {helperText}
        </FormHelperText>
      ) : null}

      {inlineSearch ? (
        <Popper
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          placement={popupPlacement?.above ? "top-start" : "bottom-start"}
          sx={{ zIndex: 1300 }}
        >
          <ClickAwayListener
            onClickAway={(event) => {
              if (inlineFieldRef.current?.contains(event.target as Node)) {
                return;
              }
              setAnchorEl(null);
              clearSearch();
            }}
          >
            <Paper
              sx={[
                {
                  border: "1px solid #9E9E9E",
                  borderRadius: "8px",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily: invoiceDesignFontFamily,
                  maxHeight: popupPlacement
                    ? `${popupPlacement.maxHeight}px`
                    : "min(287px, calc(100vh - 16px))",
                  maxWidth: "calc(100vw - 16px)",
                  mb: popupPlacement?.above ? "4px" : 0,
                  mt: popupPlacement?.above ? 0 : "4px",
                  overflow: "hidden",
                  width: popupWidth,
                },
                ...(Array.isArray(menuSx) ? menuSx : [menuSx]),
              ]}
            >
              {optionsPanel}
            </Paper>
          </ClickAwayListener>
        </Popper>
      ) : (
      <AnchoredMenu
        anchorEl={anchorEl}
        autoFocusItem={false}
        // avoidTriggerOverlap computes above/below and the list height itself.
        flip={!popupPlacement}
        placement={popupPlacement?.above ? "top-start" : "bottom-start"}
        MenuListProps={{
          "aria-labelledby": accessibleLabelId,
          disablePadding: true,
          sx: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "inherit",
            overflow: "hidden",
          },
        }}
        onClose={closeMenu}
        open={Boolean(anchorEl)}
        PaperProps={{
          sx: [
            {
              border: "1px solid #9E9E9E",
              borderRadius: "8px",
              boxSizing: "border-box",
              boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
              fontFamily: invoiceDesignFontFamily,
              maxHeight: popupPlacement ? `${popupPlacement.maxHeight}px` : "min(355px, calc(100vh - 16px))",
              maxWidth: "calc(100vw - 16px)",
              mb: popupPlacement?.above ? "4px" : 0,
              mt: popupPlacement?.above ? 0 : "4px",
              overflow: "hidden",
              width: popupWidth,
            },
            ...(Array.isArray(menuSx) ? menuSx : [menuSx]),
          ],
        }}
      >
        {searchable && !inlineSearch ? (
          <Box
            onKeyDown={(event) => {
              if (event.key !== "Escape") {
                event.stopPropagation();
              }
            }}
            sx={{ borderBottom: "1px solid #9E9E9E", p: "8px" }}
          >
            <TextField
              disabled={disabled}
              fullWidth
              inputRef={handleSearchInputRef}
              inputProps={{
                "aria-activedescendant": activeOptionId,
                "aria-autocomplete": "list",
                "aria-controls": listboxId,
                "aria-expanded": Boolean(anchorEl),
                "aria-label": searchPlaceholder,
                role: "combobox",
                style: { padding: "8px 12px" },
              }}
              onChange={({ target }) => {
                setSearch(target.value);
                setRequestedActiveOptionKey(null);
                onSearchChange?.(target.value);
              }}
              onKeyDown={handleOptionKeyDown}
              placeholder={searchPlaceholder}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontFamily: invoiceDesignFontFamily,
                  fontSize: 14,
                  height: 38,
                  minHeight: "38px !important",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#9E9E9E",
                    borderWidth: 1,
                  },
                },
                "& .MuiInputBase-input": {
                  boxSizing: "border-box",
                  lineHeight: "20px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#9E9E9E",
                },
              }}
              value={search}
            />
          </Box>
        ) : null}

        {optionsPanel}
      </AnchoredMenu>
      )}
    </Box>
  );
};

const optionRowSx = {
  backgroundColor: "transparent",
  boxSizing: "border-box",
  fontFamily: invoiceDesignFontFamily,
  fontSize: 14,
  lineHeight: "20px",
  minHeight: "41px !important",
  overflow: "hidden",
  px: "12px",
  py: "10px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover, &.Mui-focusVisible": {
    backgroundColor: "#F7F7F8",
  },
} as const;

export default InvoiceSearchableSelect;
